import type { ProviderOffer } from "../../../packages/providers/src/index.js";

export type ExploreSort = "BEST_OVERALL_VALUE" | "LOWEST_PRICE" | "RATING" | "DISTANCE" | "CANCELLATION_FLEXIBILITY";

export interface RecentSearch {
  readonly query: string;
  readonly category: string;
  readonly searchedAt: string;
}

export interface SavedTravelItem {
  readonly kind: "place" | "hotel" | "tour" | "offer" | "guide" | "livestream" | "event";
  readonly targetId: string;
  readonly title: string;
  readonly savedAt: string;
  readonly sourceReference: string;
}

export interface ExploreStateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const recentKey = "portability.explore.recent.v1";
const savedKey = "portability.saved-items.v1";
const maximumRecent = 10;

function safeArray<T>(storage: ExploreStateStorage, key: string, validate: (value: unknown) => value is T): readonly T[] {
  const raw = storage.getItem(key);
  if (raw === null) return Object.freeze([]);
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? Object.freeze(parsed.filter(validate)) : Object.freeze([]);
  } catch {
    return Object.freeze([]);
  }
}

function isRecentSearch(value: unknown): value is RecentSearch {
  return typeof value === "object" && value !== null &&
    typeof (value as RecentSearch).query === "string" && typeof (value as RecentSearch).category === "string" &&
    Number.isFinite(Date.parse((value as RecentSearch).searchedAt));
}

function isSavedTravelItem(value: unknown): value is SavedTravelItem {
  return typeof value === "object" && value !== null &&
    typeof (value as SavedTravelItem).kind === "string" && typeof (value as SavedTravelItem).targetId === "string" &&
    typeof (value as SavedTravelItem).title === "string" && typeof (value as SavedTravelItem).sourceReference === "string" &&
    Number.isFinite(Date.parse((value as SavedTravelItem).savedAt));
}

export function readRecentSearches(storage: ExploreStateStorage): readonly RecentSearch[] {
  return safeArray(storage, recentKey, isRecentSearch).slice(0, maximumRecent);
}

export function replaceRecentSearches(storage: ExploreStateStorage, searches: readonly RecentSearch[]): readonly RecentSearch[] {
  const validated = searches.filter(isRecentSearch).slice(0, maximumRecent).map((search) => Object.freeze({ ...search, query: search.query.trim() })).filter(({ query }) => Boolean(query));
  storage.setItem(recentKey, JSON.stringify(validated));
  return Object.freeze(validated);
}

export function recordRecentSearch(storage: ExploreStateStorage, search: RecentSearch): readonly RecentSearch[] {
  const query = search.query.trim();
  if (!query || !search.category.trim() || !Number.isFinite(Date.parse(search.searchedAt))) throw new Error("A recent search requires query, category, and time");
  const normalized = Object.freeze({ ...search, query });
  const prior = readRecentSearches(storage).filter((candidate) => !(candidate.query.toLocaleLowerCase() === query.toLocaleLowerCase() && candidate.category === search.category));
  const next = Object.freeze([normalized, ...prior].slice(0, maximumRecent));
  storage.setItem(recentKey, JSON.stringify(next));
  return next;
}

export function readSavedTravelItems(storage: ExploreStateStorage): readonly SavedTravelItem[] {
  return safeArray(storage, savedKey, isSavedTravelItem);
}

export function replaceSavedTravelItems(storage: ExploreStateStorage, items: readonly SavedTravelItem[]): readonly SavedTravelItem[] {
  const validated = items.filter(isSavedTravelItem).slice(0, 500).map((item) => Object.freeze({ ...item }));
  storage.setItem(savedKey, JSON.stringify(validated));
  return Object.freeze(validated);
}

export function replaceExploreState(storage: ExploreStateStorage, state: { readonly recentSearches: readonly RecentSearch[]; readonly savedItems: readonly SavedTravelItem[] }): void {
  replaceRecentSearches(storage, state.recentSearches);
  replaceSavedTravelItems(storage, state.savedItems);
}

export function toggleSavedTravelItem(storage: ExploreStateStorage, item: SavedTravelItem): readonly SavedTravelItem[] {
  if (!item.targetId.trim() || !item.title.trim() || !item.sourceReference.trim() || !Number.isFinite(Date.parse(item.savedAt))) throw new Error("Saved travel items require identity, title, source, and time");
  const prior = readSavedTravelItems(storage);
  const exists = prior.some((candidate) => candidate.kind === item.kind && candidate.targetId === item.targetId);
  const next = exists
    ? prior.filter((candidate) => !(candidate.kind === item.kind && candidate.targetId === item.targetId))
    : [Object.freeze({ ...item }), ...prior];
  storage.setItem(savedKey, JSON.stringify(next));
  return Object.freeze(next);
}

export function sortExploreOffers(offers: readonly ProviderOffer[], sort: ExploreSort): readonly ProviderOffer[] {
  const score = (offer: ProviderOffer) => {
    if (sort === "LOWEST_PRICE") return offer.price?.totalMinor ?? Number.MAX_SAFE_INTEGER;
    if (sort === "RATING") return -(offer.rating ?? -1);
    if (sort === "DISTANCE") return offer.distanceMeters ?? Number.MAX_SAFE_INTEGER;
    if (sort === "CANCELLATION_FLEXIBILITY") return ({ FLEXIBLE: 0, LIMITED: 1, UNKNOWN: 2, NON_REFUNDABLE: 3 } as const)[offer.cancellationFlexibility];
    const price = offer.price?.totalMinor ?? 0;
    const rating = offer.rating ?? 0;
    const savings = offer.memberSavings?.amountMinor ?? 0;
    return -(rating * 10_000 + savings - Math.min(price, 100_000) / 100);
  };
  return Object.freeze([...offers].sort((left, right) => score(left) - score(right) || left.title.localeCompare(right.title)));
}

export function offerMapPoint(offer: ProviderOffer): { readonly latitude: number; readonly longitude: number } | null {
  const latitude = offer.attributes.latitude;
  const longitude = offer.attributes.longitude;
  if (typeof latitude !== "number" || typeof longitude !== "number" || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return Object.freeze({ latitude, longitude });
}

export function offerShareUrls(offer: Pick<ProviderOffer, "offerId">) {
  const id = encodeURIComponent(offer.offerId.trim());
  if (!id) throw new Error("Offer id is required");
  return Object.freeze({ app: `portability://offer/${id}`, web: `https://portabilityapp.net/share/offer/${id}` });
}

export type OfferActionId = "continue" | "share" | "directions";

export interface OfferActionState {
  readonly id: OfferActionId;
  readonly label: string;
  readonly enabled: boolean;
  readonly url: string | null;
  readonly clipboardFallback: string | null;
  readonly reason: string;
}

export interface OfferActionModel {
  readonly continueAction: OfferActionState;
  readonly shareAction: OfferActionState;
  readonly directionsAction: OfferActionState;
  readonly providerHandoffAvailable: boolean;
  readonly noInventedProviderHandoff: true;
}

function coordinatesUrl(point: { readonly latitude: number; readonly longitude: number }, title: string): string {
  const query = encodeURIComponent(`${point.latitude},${point.longitude} (${title})`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

export function createOfferActionModel(offer: Pick<ProviderOffer, "offerId" | "title" | "modes" | "provenance" | "attributes">): OfferActionModel {
  const sourceUrl = offer.provenance.sourceUrl?.trim() || null;
  const actionableModes = offer.modes.filter((mode) => mode === "booking" || mode === "merchant" || mode === "redirect" || mode === "request");
  const shareUrl = offerShareUrls(offer).web;
  const point = offerMapPoint(offer as ProviderOffer);
  const directionsUrl = point === null ? null : coordinatesUrl(point, offer.title);
  const providerHandoffAvailable = actionableModes.length > 0 && sourceUrl !== null;
  return Object.freeze({
    continueAction: Object.freeze({
      id: "continue" as const,
      label: actionableModes.includes("request") && !actionableModes.some((mode) => mode === "booking" || mode === "merchant" || mode === "redirect") ? "Request with provider" : "Continue with provider",
      enabled: providerHandoffAvailable,
      url: sourceUrl,
      clipboardFallback: sourceUrl,
      reason: providerHandoffAvailable
        ? `Opens provider-supplied ${actionableModes.join("/")} handoff. Availability and payment are rechecked by the provider.`
        : "Provider did not supply an actionable handoff URL for this result."
    }),
    shareAction: Object.freeze({
      id: "share" as const,
      label: "Share",
      enabled: true,
      url: shareUrl,
      clipboardFallback: shareUrl,
      reason: "Shares the canonical Portability offer URL, or copies it if native sharing is unavailable."
    }),
    directionsAction: Object.freeze({
      id: "directions" as const,
      label: "Directions",
      enabled: directionsUrl !== null,
      url: directionsUrl,
      clipboardFallback: directionsUrl,
      reason: directionsUrl === null ? "Provider did not supply validated coordinates for directions." : "Opens external directions using provider-supplied coordinates."
    }),
    providerHandoffAvailable,
    noInventedProviderHandoff: true as const
  });
}
